import { useState, useCallback } from 'react';
import { getSubscriptionsApi, getConsumersApi } from '@/services/bolNotificationsApiClient';
import { Consumer, ConsumerCreateRequest, ConsumerPatchRequest, Subscription } from '@/api/bol-notifications';
import { useSnackbarNotification } from '@/hooks/useSnackbarNotification';

export interface UseConsumerManagementProps {
  selectedSubscriptionId: string;
}

export interface UseConsumerManagementReturn {
  consumers: Consumer[];
  subscriptions: Subscription[];
  loading: boolean;
  selectedConsumer: Consumer | null;
  setSelectedConsumer: (consumer: Consumer | null) => void;
  fetchConsumers: (page?: number, pageSize?: number) => Promise<void>;
  fetchSubscriptions: () => Promise<void>;
  createConsumer: (consumerData: ConsumerCreateRequest) => Promise<void>;
  updateConsumer: (consumerId: string, consumerData: ConsumerPatchRequest) => Promise<void>;
  deleteConsumer: (consumerId: string) => Promise<void>;
  setTotal: (total: number) => void;
  total: number;
}

export const useConsumerManagement = ({ 
  selectedSubscriptionId 
}: UseConsumerManagementProps): UseConsumerManagementReturn => {
  const [consumers, setConsumers] = useState<Consumer[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedConsumer, setSelectedConsumer] = useState<Consumer | null>(null);
  const [total, setTotal] = useState(0);
  
  const { showSuccess, showError } = useSnackbarNotification();

  const fetchSubscriptions = useCallback(async () => {
    try {
      const api = getSubscriptionsApi();
      
      const response = await api.listSubscriptions({
        limit: 100,
        offset: 0
      });
      
      setSubscriptions(response.results);
    } catch (err: unknown) {
      console.error('Error fetching subscriptions:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch subscriptions';
      showError(errorMessage);
    }
  }, [showError]);

  const fetchConsumers = useCallback(async (page: number = 0, pageSize: number = 10) => {
    if (!selectedSubscriptionId) return;

    console.log('Fetching consumers with:', { subscriptionId: selectedSubscriptionId, page, pageSize });

    setLoading(true);
    
    try {
      const api = getConsumersApi();
      
      const response = await api.listConsumers({
        subscriptionId: selectedSubscriptionId,
        limit: pageSize,
        offset: page * pageSize
      });
      
      console.log('Consumers API response:', response);
      
      setConsumers(response.results);
      setTotal(response.pagination.total);
    } catch (err: unknown) {
      console.error('Error fetching consumers:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch consumers';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [selectedSubscriptionId, showError]);

  const createConsumer = useCallback(async (consumerData: ConsumerCreateRequest) => {
    if (!selectedSubscriptionId) return;

    try {
      console.log('Creating consumer with data:', consumerData);

      const api = getConsumersApi();
      
      const createdConsumer = await api.createConsumer({
        contentType: 'application/json',
        subscriptionId: selectedSubscriptionId,
        consumerCreateRequest: consumerData,
        source: true
      });
      
      console.log('Consumer created successfully:', createdConsumer);
      
      showSuccess('Consumer created successfully');
    } catch (err: unknown) {
      console.error('Error creating consumer:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create consumer';
      showError(`Create consumer failed: ${errorMessage}`);
      throw err;
    }
  }, [selectedSubscriptionId, showSuccess, showError]);

  const updateConsumer = useCallback(async (consumerId: string, consumerData: ConsumerPatchRequest) => {
    if (!selectedSubscriptionId) return;

    try {
      console.log('Updating consumer with data:', consumerData);

      const api = getConsumersApi();
      
      const updatedConsumer = await api.patchConsumerById({
        contentType: 'application/json',
        subscriptionId: selectedSubscriptionId,
        consumerId: consumerId,
        consumerPatchRequest: consumerData
      });
      
      console.log('Consumer updated successfully:', updatedConsumer);
      
      showSuccess('Consumer updated successfully');
    } catch (err: unknown) {
      console.error('Error updating consumer:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update consumer';
      showError(`Update consumer failed: ${errorMessage}`);
      throw err;
    }
  }, [selectedSubscriptionId, showSuccess, showError]);

  const deleteConsumer = useCallback(async (consumerId: string) => {
    if (!selectedSubscriptionId) return;

    try {
      const api = getConsumersApi();
      
      await api.deleteConsumerById({
        subscriptionId: selectedSubscriptionId,
        consumerId: consumerId
      });
      
      showSuccess('Consumer deleted successfully');
    } catch (err: unknown) {
      console.error('Error deleting consumer:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete consumer';
      showError(`Delete consumer failed: ${errorMessage}`);
      throw err;
    }
  }, [selectedSubscriptionId, showSuccess, showError]);

  return {
    consumers,
    subscriptions,
    loading,
    selectedConsumer,
    setSelectedConsumer,
    fetchConsumers,
    fetchSubscriptions,
    createConsumer,
    updateConsumer,
    deleteConsumer,
    setTotal,
    total
  };
};